<script lang="ts" setup>
/**
 * @link https://idsk.gov.sk/komponenty/formularove-prvky#form-radio-buttons
 */
import { computed } from 'vue'
import { CLASS_ROOT } from './constants'
import { safeDomId } from '/@src/utils/formValidations'

export interface IDSKRadioProps {
  id?: string
  name: string
  value: string
  modelValue: string
  checked?: boolean
  hint?: string
  label?: string
  dataTestId?: string
}

export interface IDSKRadioEmits {
  (e: 'update:modelValue', val: string): string
}

const emit = defineEmits<IDSKRadioEmits>()

const props = withDefaults(defineProps<IDSKRadioProps>(), {
  id: undefined,
  checked: false,
  hint: undefined,
  label: undefined,
  dataTestId: '',
})

const safeId = safeDomId(props.id)

function handleOnInput(value: any) {
  emit('update:modelValue', value.target.value)
}
const isChecked = computed<boolean>(() => props.value === props.modelValue)
</script>

<template>
  <div :class="`${CLASS_ROOT}__item`">
    <input
      :id="safeId"
      :class="`${CLASS_ROOT}__input`"
      :name="props.name"
      :aria-label="props.label"
      type="radio"
      :value="props.value"
      :checked="isChecked"
      v-bind="$attrs"
      :data-test-id="'radio' + '-' + dataTestId"
      @input="() => emit('update:modelValue', props.value)"
    />
    <label :class="['govuk-label', `${CLASS_ROOT}__label`]" :for="safeId">
      <slot />
    </label>
    <span
      v-if="props.hint"
      :id="`${safeId}-item-hint`"
      :class="`govuk-hint ${CLASS_ROOT}__hint`"
    >
      {{ props.hint }}
    </span>
  </div>
  <div
    v-if="$slots['visible-content']"
    :class="[
      `${CLASS_ROOT}__conditional visible-content`,
      {
        [`${CLASS_ROOT}__conditional--hidden`]: !isChecked,
      },
    ]"
  >
    <slot name="visible-content"></slot>
  </div>
  <div
    v-if="$slots['hidden-content']"
    :id="`${id}-conditional`"
    :class="[
      `${CLASS_ROOT}__conditional`,
      {
        [`${CLASS_ROOT}__conditional--hidden`]: !isChecked,
      },
    ]"
  >
    <slot name="hidden-content"></slot>
  </div>
</template>

<style scoped lang="scss">
.govuk-radios__conditional {
  &.visible-content {
    margin-bottom: 0;
    padding-bottom: 20px;
  }
  .js-enabled &--hidden {
    &.visible-content {
      margin-bottom: 20px;
      display: block;
    }
  }
}

.govuk-radios__item {
  ::before {
    transition: all 0.195s ease-in;
  }
  &:hover {
    .govuk-label,
    .govuk-body {
      font-weight: 600;
    }

    ::before {
      margin-right: 0px;
      box-shadow: inset 0 0 0 3px;
    }
  }
}
</style>
